import { useMutation } from "@apollo/client/react";
import mixpanel from "mixpanel-browser";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useNoteUsage } from "./use-note-usage";
import { alert } from "../components/common/Alert";
import TemplateSelectContext from "../context/templateSelectContext";
import { RETRY_FAILED_AUTO_SOAP } from "../graphql/mutations/RetryFailedAutoSoap";

interface Note {
  uuid: string;
  jobType?: string;
}

interface Template {
  uuid: string;
  value?: string;
}

export function useTemplateHandler(note: Note) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { shouldUseAutoSoaps } = useNoteUsage();
  const navigate = useNavigate();

  const [retryFailedAutoSoap] = useMutation(RETRY_FAILED_AUTO_SOAP);

  const handleTemplateSelect = async (template: Template) => {
    try {
      await retryFailedAutoSoap({
        variables: {
          noteUuid: note.uuid,
          jobType: template?.value || "template", // default templates have a value, auto templates don't and just use "template"
          templateUuid: template.uuid,
          skipTranscription: true,
        },
      });

      alert(
        "success",
        "Template applied successfully! Your note is now reprocessing.",
      );
      navigate(`/dashboard`);
      mixpanel.track("Reprocessed note as template.", {
        noteUuid: note.uuid,
        templateUuid: template.uuid,
        jobType: note.jobType,
      });
    } catch (error) {
      alert("error", "Failed to apply template. Please try again.");
      mixpanel.track("Failed to apply template", {
        noteUuid: note.uuid,
        templateUuid: template.uuid,
        jobType: note.jobType,
        error:
          error instanceof Error ? error.message : "Unknown error",
      });
    }

    setIsModalOpen(false);
  };

  const openTemplateModal = () => {
    setIsModalOpen(true);
  };

  const closeTemplateModal = () => {
    setIsModalOpen(false);
  };

  return {
    isModalOpen,
    shouldUseAutoSoaps,
    handleTemplateSelect,
    openTemplateModal,
    closeTemplateModal,
  };
}

// Legacy template handler for non-auto-soap usage
export function useLegacyTemplateHandler(note: Note | null) {
  const {
    setShouldShowTemplateSelectModal,
    setUuidOfNoteToOverwriteWithTemplate,
  } = useContext(TemplateSelectContext) as {
    setShouldShowTemplateSelectModal: (value: boolean) => void;
    setUuidOfNoteToOverwriteWithTemplate: (value: string) => void;
  };

  const handleTemplateClick = () => {
    setShouldShowTemplateSelectModal(true);
    if (note?.uuid) {
      setUuidOfNoteToOverwriteWithTemplate(note?.uuid);
    }
  };

  return {
    handleTemplateClick,
  };
}
